import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { twcn } from '@web/ui/core/utils';

interface DrawerProps {
  open: boolean;
  /** Called when the drawer requests close (×, scrim click, or Esc). */
  onClose: () => void;
  /** Accessible name for the dialog — also rendered as a visually-hidden title. */
  ariaLabel: string;
  /** Optional extra classes for the panel (e.g. a wider width). */
  className?: string;
  children: ReactNode;
}

/**
 * Drawer — right-docked overlay panel built on Radix Dialog, so focus-trap,
 * `Esc`, scrim-click-to-close, scroll-lock and focus restoration come for free.
 * Slides in via the shared `drawer-in`/`scrim-in` animations (both no-op under
 * `prefers-reduced-motion`). The single interaction model for voucher detail and
 * any future side-panel content; the caller owns the `open` state.
 */
export function Drawer({
  open,
  onClose,
  ariaLabel,
  className,
  children,
}: Readonly<DrawerProps>): React.JSX.Element {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-scrim bg-foreground/30 backdrop-blur-sm animate-scrim-in motion-reduce:animate-none" />
        <DialogPrimitive.Content
          aria-label={ariaLabel}
          className={twcn(
            'fixed inset-y-0 right-0 z-drawer flex w-drawer-w max-w-full flex-col',
            'border-l border-border-soft bg-surface shadow-overlay',
            'animate-drawer-in motion-reduce:animate-none focus:outline-none',
            className,
            // The device cutout on the edges the panel touches (`index.html` is
            // `viewport-fit=cover`). After `className`: it is invariant, and a consumer's
            // padding must not erase it in the merge. Top, right and bottom always touch the
            // screen; the left edge only on a phone, where `max-w-full` makes the panel the
            // whole width — `env()` is the viewport's, so on a wider screen a left cutout is
            // on the far side and must not eat the content. Outside `viewport-fit=cover`
            // every `env()` resolves 0 and nothing moves.
            'pt-[env(safe-area-inset-top,0px)] pr-[env(safe-area-inset-right,0px)] pb-[env(safe-area-inset-bottom,0px)] max-sm:pl-[env(safe-area-inset-left,0px)]',
          )}
        >
          <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
